import React, { useContext, useState, useEffect } from 'react';
import { onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';
import { auth, googleProvider, db } from '../firebase';
import { doc, getDoc, setDoc, writeBatch } from 'firebase/firestore';

const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshCurrentUser = async () => {
    const user = auth.currentUser;
    if (user) {
      const userDocRef = doc(db, "users", user.uid);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        const profileData = userDocSnap.data();
        const updatedUser = { ...user }; // Create a mutable copy
        updatedUser.role = profileData.role;
        updatedUser.status = profileData.status;
        updatedUser.displayName = profileData.displayName;
        updatedUser.department = profileData.department;
        updatedUser.position = profileData.position;
        updatedUser.phoneNumber = profileData.phoneNumber;
        updatedUser.phoneNumbers = profileData.phoneNumbers;
        updatedUser.zaloNumber = profileData.zaloNumber;
        updatedUser.level = profileData.level;
        // Loại nhân viên (fulltime/parttime) — dùng cho dashboard chuyên cần & giải thích thưởng
        updatedUser.employmentType = profileData.employmentType || 'parttime';
        setCurrentUser(updatedUser);
      }
    }
  };

  async function loginWithGoogle(inviteId = null) {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;
    const userDocRef = doc(db, "users", user.uid);
    const userDocSnap = await getDoc(userDocRef);

    if (userDocSnap.exists()) {
      return result;
    }

    if (inviteId) {
      const inviteDocRef = doc(db, "invitations", inviteId);
      const inviteDocSnap = await getDoc(inviteDocRef);

      if (!inviteDocSnap.exists() || inviteDocSnap.data().status !== 'pending') {
        await signOut(auth);
        throw new Error("Link mời không hợp lệ hoặc đã được sử dụng.");
      }

      if (inviteDocSnap.data().email !== user.email) {
        await signOut(auth);
        throw new Error("Email đăng nhập không khớp với email trong lời mời.");
      }

      const newUserProfile = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        role: 'employee',
        status: 'active',
        department: '',
        position: '',
        phoneNumber: '',
        phoneNumbers: [],
        zaloNumber: '',
        level: 1
      };

      const batch = writeBatch(db);
      batch.set(userDocRef, newUserProfile);
      batch.update(inviteDocRef, { status: 'completed', acceptedAt: new Date() });
      await batch.commit();

      const updatedUser = { ...user };
      updatedUser.role = newUserProfile.role;
      updatedUser.status = newUserProfile.status;
      updatedUser.department = newUserProfile.department;
      updatedUser.position = newUserProfile.position;
      updatedUser.phoneNumber = newUserProfile.phoneNumber;
      updatedUser.phoneNumbers = newUserProfile.phoneNumbers;
      updatedUser.zaloNumber = newUserProfile.zaloNumber;
      updatedUser.level = newUserProfile.level;
      setCurrentUser(updatedUser);

    } else {
      await signOut(auth);
      throw new Error("Tài khoản của bạn chưa được mời tham gia hệ thống.");
    }
    return result;
  }

  function logout() {
    return signOut(auth);
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          const profileData = userDocSnap.data();
          const updatedUser = { ...user };
          updatedUser.role = profileData.role;
          updatedUser.status = profileData.status;
          updatedUser.displayName = profileData.displayName;
          updatedUser.department = profileData.department;
          updatedUser.position = profileData.position;
          updatedUser.phoneNumber = profileData.phoneNumber;
          updatedUser.phoneNumbers = profileData.phoneNumbers;
          updatedUser.zaloNumber = profileData.zaloNumber;
          updatedUser.level = profileData.level;
          setCurrentUser(updatedUser);
        } else {
          setCurrentUser(null);
        }
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const isLocked = currentUser?.status === 'locked';

  const value = {
    currentUser,
    loading,
    isLocked,
    loginWithGoogle,
    logout,
    refreshCurrentUser,
    refreshUserData: refreshCurrentUser
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
